﻿module Client.ContractPage.CostOfConstruction.Expanded.CostDetailTable

open FSharpPlus
open Client
open Fable.React
open Fable.React.Props
open PortalLibs.Client.Components
open PortalLibs.Client.Util
open ContractPageModel
open Model
open Browser
open Fulma
open Fable.FontAwesome
open PortalLibs.Shared
open Shared
open PortalLibs.Client.Components.NewTableNext
open Feliz

let NewTableNextSpecific =
  NewTableNextGeneric<JobCostDetail, JobCostDetailField, unit>

let CostDetailTableView =
  React.memo (
    "CostDetailTableView",
    (fun (props: {| details: JobCostDetail list
                    isLoading: bool
                    jobCostId: JobCostId
                    attachments: Map<JobCostDetailId, JobCostDetailAttachment list> option
                    tableState: NewTableNext.Model<JobCostDetail, JobCostDetailField, unit>
                    isSuperUser: bool
                    Key: string
                    dispatch: Msg -> unit |}) ->
      let columns =
        [ { Header = Text "Posted Date"
            Id = PostedDate
            Align = Left
            Value = ValueType.Text(fun (detail: JobCostDetail) -> detail.PostedDate |> prettyPrintDateShort)
            Sortable = ColumnSort.ValueOrExternal
            Draggable = false
            MinWidth = None
            Footer = None
            InitiallyVisible = true }

          { Header = Text "Actual Date"
            Id = ActualDate
            Align = Left
            Value = ValueType.Text(fun (detail: JobCostDetail) -> detail.ActualDate |> prettyPrintDateShort)
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = None
            InitiallyVisible = true
            Footer = None }

          { Header = Text "Description"
            Id = Description
            Align = Left
            Value =
              ValueType.Custom (fun (costDetail: JobCostDetail) ->
                div [ Style [
                        Display DisplayOptions.Flex
                        JustifyContent "space-between"
                        AlignItems AlignItemsOptions.Center
                        Width "100%"
                      ] ] [
                  div [ Style [
                          FlexShrink "1"
                          WhiteSpace WhiteSpaceOptions.Normal
                        ] ] [
                    emptyStrTagIfNone costDetail.Description
                  ]
                  div [] [
                    match props.attachments with
                    | None ->
                      Icon.icon [ Icon.Props [ Style [ Color "gray" ] ] ] [
                        Fa.i [ Fa.Solid.Spinner; Fa.Pulse ] []
                      ]
                    | Some attachments ->
                      let attachmentCount =
                        Map.tryFind costDetail.Id attachments
                        |>> (fun attachments -> attachments.Length)
                        |> Option.defaultValue 0

                      let content =
                        div [] [
                          Fa.stack [] [
                            Fa.i [ Fa.Props [
                                     Style [
                                       Color(
                                         if attachmentCount > 0 then
                                           "green"
                                         else
                                           "gainsboro"
                                       )
                                     ]
                                   ]
                                   Fa.Stack2x
                                   Fa.Regular.File ] []
                            Fa.i [ Fa.Props [
                                     Style [
                                       Color(
                                         if attachmentCount > 0 then
                                           "black"
                                         else
                                           "gainsboro"
                                       )
                                       PaddingTop "3px"
                                       PaddingRight "1px"
                                       FontSize "0.75rem"
                                     ]
                                   ]
                                   Fa.Stack1x ] [
                              strong [ Style [
                                         Color(
                                           if attachmentCount > 0 then
                                             "black"
                                           else
                                             "gainsboro"
                                         )
                                       ] ] [
                                attachmentCount |> string |> str
                              ]
                            ]
                          ]
                        ]

                      if attachmentCount > 0 then
                        a [ OnClick (fun _ ->
                              Some(props.jobCostId, costDetail.Id)
                              |> ShowAttachmentsModal
                              |> props.dispatch) ] [
                          content
                        ]
                      else
                        div [ Style [
                                Display DisplayOptions.Flex
                                JustifyContent "center"
                                Width "100%"
                                Padding "1px 0"
                              ] ] [
                          content
                        ]
                  ]
                ])
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = None
            InitiallyVisible = true
            Footer = Some(Footer.Text "Total:") }
          { Header = Text "Units"
            Id = Units
            Align = Right
            Value = ValueType.Text(fun (detail: JobCostDetail) -> detail.Units |> sprintf "%.3f")
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = None
            InitiallyVisible = true
            Footer = Some(Total(sprintf "%.3f")) }
          { Header = Text "Hours"
            Id = Hours
            Align = Right
            Value = ValueType.Text(fun (detail: JobCostDetail) -> detail.Hours |> sprintf "%.1f")
            Sortable = ValueOrExternal
            Draggable = false
            MinWidth = None
            InitiallyVisible = true
            Footer = Some(Total(sprintf "%.1f")) }

          if props.isSuperUser then
            { Header = Text "Cost"
              Id = Cost
              Align = Right
              Value =
                ValueType.Custom (fun (detail: JobCostDetail) ->
                  div [ Style [
                          FlexGrow "1"
                          TextAlign TextAlignOptions.Right
                        ] ] [
                    span [ if detail.Cost < 0.0 then
                             Style [ Color "red" ] ] [
                      str (
                        detail.Cost
                        |> formatNumber false Parenthesis false AllTwoDigits
                      )
                    ]
                  ])
              Sortable = ValueOrExternal
              Draggable = false
              MinWidth = None
              InitiallyVisible = true
              Footer = Some(Total(DisplayNumber.AsCurrency.std)) }

          ]

      div [ ClassName "costofconstruction-table"
            Style [
              MaxWidth "1200px"
              Margin "auto"
            ] ] [
        NewTableNextSpecific
          {| Config =
              { Model = props.tableState
                Columns = columns
                ColumnSelection = false
                Items = props.details
                ItemKey = fun item -> item.Id.ToString()
                SubRows = None
                Selection = [], false
                Paging =
                  Paged
                    { HidePaginationUnder1Page = true
                      PageSizeOptions = None }
                Key = "CostOfConstructionCostDetailTable"
                RowClick = None
                DataLoad = OnDemand
                StickyHeader = None
                Dispatch =
                  (fun msg ->
                    TableStateChanged(props.jobCostId, msg)
                    |> props.dispatch) } |}
      ]),
    (fun (props: {| details: JobCostDetail list
                    isLoading: bool
                    jobCostId: JobCostId
                    attachments: Map<JobCostDetailId, JobCostDetailAttachment list> option
                    tableState: NewTableNext.Model<JobCostDetail, JobCostDetailField, unit>
                    isSuperUser: bool
                    Key: string
                    dispatch: Msg -> unit |}) -> $"CostDetailTableView-%s{props.Key}")
  )

let View =
  React.memo (
    "CostDetailTableAndSearchView",
    (fun (props: {| details: JobCostDetail list
                    contract: Contract
                    jobCost: JobCost
                    searchModels: Map<JobCostId, string option>
                    attachments: Map<JobCostDetailId, JobCostDetailAttachment list> option
                    Key: string
                    tableStates: Map<JobCostId, NewTableNext.Model<JobCostDetail, JobCostDetailField, unit>>
                    isSuperUser: bool
                    dispatch: Msg -> unit |}) ->
      let searchViewChanged =
        React.useCallback (
          ((fun msg -> SearchValueChanged(props.jobCost.Id, msg))
           >> props.dispatch),
          [||]
        )

      let searchValue =
        searchValue props.searchModels props.jobCost.Id

      let tableState =
        tableState props.tableStates props.jobCost.Id

      tr [ Style [ FontSize Config.smallTextSize ] ] [
        td [ ColSpan 11 ] [
          div [] [
            div [ Style [
                    Display DisplayOptions.Flex
                    AlignItems AlignItemsOptions.Center
                    JustifyContent "flex-end"
                    Padding "5px 0 0 0"
                    Margin "0 0 -3px 0"
                  ] ] [
              a [ OnClick (fun _ ->
                    window.location.href <-
                      (sprintf
                        "/costdetailexport/%i/%s"
                        props.jobCost.Id
                        (sprintf
                          "%s-%s-%s.xlsx"
                          (props.contract.Description
                           |> Option.defaultValue "")
                          (props.jobCost.PhaseItemDescription
                           |> Option.defaultValue "")
                          (props.jobCost.Description
                           |> Option.defaultValue "")
                         |> FileUtil.cleanupFileName))) ] [
                Icon.icon [ Icon.Size ISize.IsLarge
                            Icon.Props [ Style [ Color "#3273dc" ] ] ] [
                  Fa.i [ Fa.Solid.FileExcel
                         Fa.Size Fa.ISize.Fa2x ] []
                ]
              ]
              SearchboxNew.SearchboxNew
                {| Value = searchValue
                   IsLoading = false
                   IconVisible = false
                   ValueChanged = searchViewChanged
                   Key = $"CostOfConstruction-%i{props.jobCost.Id}-Searchbox"
                   ShouldFocus = false |}
            ]
            CostDetailTableView
              {| details = props.details
                 attachments = props.attachments
                 isLoading = false
                 isSuperUser = props.isSuperUser
                 jobCostId = props.jobCost.Id
                 tableState = tableState
                 dispatch = props.dispatch
                 Key = $"CostDetailTable-%s{props.jobCost.Id.ToString()}" |}
          ]
        ]
      ]),
    (fun (props: {| details: JobCostDetail list
                    contract: Contract
                    jobCost: JobCost
                    searchModels: Map<JobCostId, string option>
                    attachments: Map<JobCostDetailId, JobCostDetailAttachment list> option
                    Key: string
                    tableStates: Map<JobCostId, NewTableNext.Model<JobCostDetail, JobCostDetailField, unit>>
                    isSuperUser: bool
                    dispatch: Msg -> unit |}) -> $"CostDetailTableAndSearchView-%s{props.Key}")
  )